// components/Sender/TemplateBulkRuntimeDialog.js
import React, { useMemo, useState } from "react";

function _toggle(set, v) {
  const next = new Set(set);
  if (next.has(v)) next.delete(v);
  else next.add(v);
  return next;
}

// Канонизация к UPPER_SNAKE. Знает все алиасы:
// email/EMAIL/e-mail → EMAIL
// contact/contact_form/cf/CONTACT_FORM → CONTACT_FORM
// calendly/CALENDLY → CALENDLY
// contactAndEmail/emailandcontact/BOTH/... → EMAIL_AND_CONTACT
const _CHANNEL_ALIASES = {
  "EMAIL": "EMAIL",
  "E-MAIL": "EMAIL",
  "E_MAIL": "EMAIL",
  "MAIL": "EMAIL",
  "EMAIL_ONLY": "EMAIL",

  "CONTACT": "CONTACT_FORM",
  "CONTACT_FORM": "CONTACT_FORM",
  "CF": "CONTACT_FORM",
  "CONTACT_FORM_ONLY": "CONTACT_FORM",

  "CALENDLY": "CALENDLY",
  "CALENDLY_ONLY": "CALENDLY",

  "EMAIL_AND_CONTACT": "EMAIL_AND_CONTACT",
  "CONTACT_AND_EMAIL": "EMAIL_AND_CONTACT",
  "EMAILANDCONTACT": "EMAIL_AND_CONTACT",
  "CONTACTANDEMAIL": "EMAIL_AND_CONTACT",
  "BOTH": "EMAIL_AND_CONTACT",
};

function _normChannel(raw) {
  const s = String(raw || "").trim().toUpperCase();
  if (!s) return "EMAIL";
  return _CHANNEL_ALIASES[s] || s;
}

// Ключ в runtime_defaults для канала.
// EMAIL_AND_CONTACT наследует EMAIL-дефолт (email-шаг идёт первым).
const _RUNTIME_KEY = {
  EMAIL: "EMAIL",
  CONTACT_FORM: "CONTACT_FORM",
  CALENDLY: "CALENDLY",
  EMAIL_AND_CONTACT: "EMAIL",
};

const _CHANNEL_META = {
  EMAIL:             { icon: "📧", label: "EMAIL" },
  CONTACT_FORM:      { icon: "📝", label: "CONTACT_FORM" },
  CALENDLY:          { icon: "📅", label: "CALENDLY" },
  EMAIL_AND_CONTACT: { icon: "✉️", label: "EMAIL + CONTACT" },
};

function _channelLabel(ch) {
  const c = _CHANNEL_META[ch];
  return c ? `${c.icon} ${c.label}` : `❔ ${ch}`;
}
function _channelIcon(ch) {
  return _CHANNEL_META[ch]?.icon || "❔";
}

export function TemplateBulkRuntimeDialog({
  blocks, runtimes, onApply, onClose,
}) {
  // ─── Плоский список воркеров с их текущим состоянием ───
  const allWorkers = useMemo(() => {
    const list = [];
    (blocks || []).forEach((b, blockIdx) => {
      (b.workers || []).forEach((w) => {
        const channel = _normChannel(w.channel);
        const explicit = w.runtime_id || null;
        const rtKey = _RUNTIME_KEY[channel] || "EMAIL";
        const inherited = (b.runtime_defaults || {})[rtKey] || null;
        const effective = explicit || inherited;
        list.push({
          key: `${blockIdx}:${w.worker_name}:${channel}`,
          blockIdx,
          workerName: w.worker_name,
          channel,
          explicit,
          inherited,
          effective,
        });
      });
    });
    return list;
  }, [blocks]);

  // ─── Уникальные значения для тегов-фильтров ───
  const allBlockNums = useMemo(() => {
    const s = new Set();
    allWorkers.forEach((w) => s.add(w.blockIdx));
    return Array.from(s).sort((a, b) => a - b);
  }, [allWorkers]);

  const allChannels = useMemo(() => {
    const s = new Set();
    allWorkers.forEach((w) => s.add(w.channel));
    return Array.from(s);
  }, [allWorkers]);

  const allEffectiveRuntimes = useMemo(() => {
    const s = new Set();
    allWorkers.forEach((w) => s.add(w.effective || "__none__"));
    return Array.from(s);
  }, [allWorkers]);

  // ─── Фильтры. Блоки по умолчанию — все выделены ───
  const [blockSel, setBlockSel] = useState(() => {
    const init = new Set();
    (blocks || []).forEach((_, i) => init.add(i));
    return init;
  });
  const [chSel, setChSel] = useState(new Set());
  const [rtSel, setRtSel] = useState(new Set());
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");

  // ─── Режим и target ───
  const [mode, setMode] = useState("all");
  const [count, setCount] = useState(10);
  const [target, setTarget] = useState("__inherit__");

  const targetRuntimeId = target === "__inherit__" ? null : target;

  // ─── Фильтрованный список ───
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allWorkers.filter((w) => {
      if (blockSel.size && !blockSel.has(w.blockIdx)) return false;
      if (chSel.size && !chSel.has(w.channel)) return false;
      if (rtSel.size && !rtSel.has(w.effective || "__none__")) return false;
      if (stateFilter === "explicit" && !w.explicit) return false;
      if (stateFilter === "inherited" && !w.inherited) return false;
      if (stateFilter === "unset" && w.effective) return false;
      if (q && !w.workerName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allWorkers, blockSel, chSel, rtSel, stateFilter, search]);

  // ─── Кого реально применим ───
  const toUpdate = useMemo(() => {
    const reallyChange = filtered.filter((w) => w.explicit !== targetRuntimeId);
    if (mode === "all") return reallyChange;
    if (mode === "first") return reallyChange.slice(0, Math.max(0, count));
    if (mode === "random") {
      const arr = [...reallyChange];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr.slice(0, Math.max(0, count));
    }
    return reallyChange;
  }, [filtered, targetRuntimeId, mode, count]);

  const totalMatched = filtered.length;
  const alreadySameCount = filtered.length - toUpdate.length;

  const apply = () => {
    if (!toUpdate.length) return;
    const changes = toUpdate.map((w) => ({
      blockIdx: w.blockIdx,
      workerName: w.workerName,
      runtimeId: targetRuntimeId,
    }));
    onApply(changes);
    onClose();
  };

  const resetFilters = () => {
    const initBlocks = new Set();
    (blocks || []).forEach((_, i) => initBlocks.add(i));
    setBlockSel(initBlocks);
    setChSel(new Set());
    setRtSel(new Set());
    setSearch("");
    setStateFilter("all");
  };

  const runtimeChoices = (runtimes || []).filter((r) => r.is_enabled !== false);

  const renderCurrent = (w) => {
    if (w.explicit) return { text: w.explicit, color: "#0891b2" };
    if (w.inherited) return { text: `↳ ${w.inherited}`, color: "#6b7280" };
    return { text: "— любой —", color: "#9ca3af" };
  };
  const renderNew = (w) => {
    if (targetRuntimeId === null) {
      if (w.inherited) return `↳ ${w.inherited}`;
      return "— любой —";
    }
    return targetRuntimeId;
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <div style={headerBar}>
          <div>
            <h3 style={{ margin: 0 }}>🎯 Массово назначить runtime</h3>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Фильтры → подсчёт → режим (все / первые N / случайные N)
            </div>
          </div>
          <button className="btn-cancel" onClick={onClose}>✕</button>
        </div>

        <div style={body}>
          {/* ═══════ Фильтры ═══════ */}
          <div style={filterGrid}>
            <FilterGroup
              label="📦 Блоки"
              options={allBlockNums}
              renderLabel={(i) => `#${i + 1}`}
              selected={blockSel}
              onToggle={(v) => setBlockSel(_toggle(blockSel, v))}
            />
            <FilterGroup
              label="📮 Каналы"
              options={allChannels}
              renderLabel={(ch) => _channelLabel(ch)}
              selected={chSel}
              onToggle={(v) => setChSel(_toggle(chSel, v))}
            />
            <FilterGroup
              label="🖥 Текущий runtime"
              options={allEffectiveRuntimes}
              renderLabel={(rt) => rt === "__none__" ? "— любой —" : rt}
              selected={rtSel}
              onToggle={(v) => setRtSel(_toggle(rtSel, v))}
            />
          </div>

          {/* ═══════ Поиск + state-фильтр ═══════ */}
          <div style={{ display: "flex", gap: 12, alignItems: "center",
                        flexWrap: "wrap", marginTop: 10 }}>
            <input className="form-input" placeholder="🔍 Поиск по имени…"
                   style={{ flex: 1, minWidth: 240 }}
                   value={search}
                   onChange={(e) => setSearch(e.target.value)} />
            <select className="form-input"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    style={{ width: 220 }}>
              <option value="all">Показывать: все</option>
              <option value="explicit">только явные</option>
              <option value="inherited">только наследуемые</option>
              <option value="unset">только без runtime</option>
            </select>
            <button className="btn-cancel" onClick={resetFilters}
                    style={{ fontSize: 12, padding: "4px 10px" }}>
              Сбросить
            </button>
          </div>

          {/* ═══════ Режим и назначение ═══════ */}
          <div style={{
            marginTop: 16, padding: 14, background: "#f9fafb",
            borderRadius: 10, border: "1px solid #e5e7eb",
          }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap",
                          alignItems: "center" }}>
              <div style={{ fontWeight: 700 }}>Режим:</div>
              <label style={radioLabel}>
                <input type="radio" checked={mode === "all"}
                       onChange={() => setMode("all")} />
                Все подходящие ({filtered.filter(
                  (w) => w.explicit !== targetRuntimeId).length})
              </label>
              <label style={radioLabel}>
                <input type="radio" checked={mode === "first"}
                       onChange={() => setMode("first")} />
                Первые
              </label>
              <label style={radioLabel}>
                <input type="radio" checked={mode === "random"}
                       onChange={() => setMode("random")} />
                Случайные
              </label>
              {(mode === "first" || mode === "random") && (
                <input className="form-input" type="number" min={1}
                       max={totalMatched}
                       style={{ width: 100 }}
                       value={count}
                       onChange={(e) =>
                         setCount(Math.max(1, parseInt(e.target.value, 10) || 1))} />
              )}
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap",
                          alignItems: "center", marginTop: 10 }}>
              <div style={{ fontWeight: 700 }}>Назначить:</div>
              <select className="form-input"
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                      style={{ minWidth: 280 }}>
                <option value="__inherit__">
                  ↳ Наследовать от блока (сбросить override)
                </option>
                <option disabled>──────────</option>
                {runtimeChoices.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.kind === "android" ? "📱" : "🌐"} {r.id}
                  </option>
                ))}
              </select>
            </div>

            <div style={{
              marginTop: 10, fontSize: 13, lineHeight: 1.6,
              color: "#374151",
            }}>
              <b>Итого будет обновлено:</b>{" "}
              <span style={{
                color: toUpdate.length > 0 ? "#16a34a" : "#9ca3af",
                fontWeight: 700,
              }}>{toUpdate.length}</span>{" "}
              из {allWorkers.length} воркеров
              {" "}· под фильтр попало {totalMatched}
              {targetRuntimeId === null
                ? " → наследовать (сброс override)"
                : (
                  <span>
                    {" → "}
                    <b style={{ fontFamily: "Consolas, monospace",
                                color: "#0891b2" }}>
                      {targetRuntimeId}
                    </b>
                  </span>
                )}
              {alreadySameCount > 0 && (
                <span style={{ color: "#6b7280", marginLeft: 8 }}>
                  · уже так: {alreadySameCount}
                </span>
              )}
              {totalMatched === 0 && (
                <span style={{ color: "#b45309", marginLeft: 8 }}>
                  · под фильтр никто не попал
                </span>
              )}
            </div>
          </div>

          {/* ═══════ Превью со скроллом ═══════ */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
              Превью изменений ({Math.min(toUpdate.length, 20)} из {toUpdate.length})
            </div>
            <div style={{
              maxHeight: 280, overflow: "auto",
              background: "#fff", border: "1px solid #e5e7eb",
              borderRadius: 8, padding: 8,
            }}>
              {toUpdate.length === 0 ? (
                <div style={{ color: "#9ca3af", padding: 10, fontSize: 13 }}>
                  {filtered.length === 0
                    ? "Ничего не подходит под фильтры."
                    : "Все уже в нужном состоянии — менять нечего."}
                </div>
              ) : (
                <>
                  {toUpdate.slice(0, 20).map((w) => {
                    const cur = renderCurrent(w);
                    return (
                      <div key={w.key} style={{
                        display: "grid",
                        gridTemplateColumns: "44px 22px 1fr auto auto auto",
                        gap: 8, alignItems: "center",
                        padding: "5px 8px", fontSize: 12,
                        fontFamily: "Consolas, monospace",
                        borderBottom: "1px solid #f3f4f6",
                      }}>
                        <span style={{ color: "#6b7280", fontWeight: 700 }}>
                          #{w.blockIdx + 1}
                        </span>
                        <span style={{ fontSize: 12 }}>
                          {_channelIcon(w.channel)}
                        </span>
                        <span style={{
                          overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap", color: "#111827",
                        }}>{w.workerName}</span>
                        <span style={{ color: cur.color, fontSize: 11 }}>
                          {cur.text}
                        </span>
                        <span style={{ color: "#9ca3af" }}>→</span>
                        <span style={{
                          color: "#16a34a", fontWeight: 700, fontSize: 11,
                        }}>
                          {renderNew(w)}
                        </span>
                      </div>
                    );
                  })}
                  {toUpdate.length > 20 && (
                    <div style={{ color: "#6b7280", padding: 6, fontSize: 12 }}>
                      … и ещё {toUpdate.length - 20}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div style={footer}>
          <button className="btn-cancel" onClick={onClose}>Отмена</button>
          <button className="btn-primary" disabled={!toUpdate.length}
                  onClick={apply}>
            {targetRuntimeId === null
              ? `↳ Сбросить override у ${toUpdate.length}`
              : `🎯 Назначить ${toUpdate.length} → ${targetRuntimeId}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, options, renderLabel, selected, onToggle }) {
  if (!options.length) return null;
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6,
                    color: "#374151" }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {options.map((opt) => {
          const checked = selected.has(opt);
          return (
            <button key={String(opt)}
                    onClick={() => onToggle(opt)}
                    style={{
                      padding: "4px 10px", borderRadius: 6, fontSize: 11,
                      border: checked
                        ? "1px solid #3b82f6" : "1px solid #e5e7eb",
                      background: checked ? "#eff6ff" : "#fff",
                      color: checked ? "#1d4ed8" : "#374151",
                      cursor: "pointer", fontWeight: checked ? 700 : 400,
                      fontFamily: "inherit",
                    }}>
              {renderLabel(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
  backdropFilter: "blur(3px)", zIndex: 1400,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};
const card = {
  background: "#fff", borderRadius: 14, width: "100%",
  maxWidth: 900, maxHeight: "92vh", display: "flex",
  flexDirection: "column", overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,.35)",
};
const headerBar = {
  padding: "16px 20px", borderBottom: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "space-between", alignItems: "center",
};
const body = { flex: 1, overflow: "auto", padding: 20 };
const footer = {
  padding: "12px 20px", borderTop: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "flex-end", gap: 8,
};
const filterGrid = {
  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
  gap: 14, marginBottom: 10,
};
const radioLabel = {
  display: "flex", gap: 6, alignItems: "center",
  fontSize: 13, color: "#374151", cursor: "pointer",
};